import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCRM } from '../../contexts/CRMContext';
import { useBranding } from '../../contexts/BrandingContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import FeatureGate from './FeatureGate';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useDialog, DialogHeader, DialogBody, DialogFooter } from '../kibo-ui/dialog';
import { Separator } from '../ui/separator';
import crmApi, { agencyAPI } from '../../utils/crmApi';

// Get API URL for file attachments (relative URLs need base URL)
const getApiUrl = () => {
  const apiUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5000').replace(/\/+$/, '');
  return apiUrl;
};
import { 
  ChatBubbleLeftRightIcon,
  UserGroupIcon,
  PlusIcon,
  PaperAirplaneIcon,
  HashtagIcon,
  BellIcon,
  PaperClipIcon,
  MagnifyingGlassIcon,
  LockClosedIcon,
  SparklesIcon,
  ClockIcon,
  FaceSmileIcon,
  ArrowPathIcon,
  UserPlusIcon,
  XMarkIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const CollaborationHub = () => {
  const { user, currentAgency } = useCRM();
  const { branding } = useBranding();
  const { hasFeature } = useSubscription();
  const { openDialog, closeDialog } = useDialog();
  
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageContent, setMessageContent] = useState('');
  const [sending, setSending] = useState(false);
  const [overview, setOverview] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
  const [messageLoading, setMessageLoading] = useState(false);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [showMembersDialog, setShowMembersDialog] = useState(false);
  const [managingMembers, setManagingMembers] = useState(false);
  const [deletingChannel, setDeletingChannel] = useState(false);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (currentAgency?._id && hasFeature('advancedIntegrations')) {
      loadOverview();
      loadChannels();
    }
  }, [currentAgency?._id, hasFeature]);

  useEffect(() => {
    if (selectedChannel) {
      loadMessages(selectedChannel._id);
      // Poll for new messages every 5 seconds
      const interval = setInterval(() => {
        loadMessages(selectedChannel._id, false);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedChannel]);

  useEffect(() => {
    // Auto-scroll to bottom when new messages arrive and user is at bottom
    if (messagesEndRef.current && isScrolledToBottom) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isScrolledToBottom]);

  useEffect(() => {
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 150)}px`;
    }
  }, [messageContent]);

  const handleScroll = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
      setIsScrolledToBottom(isAtBottom);
    }
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const loadOverview = async () => {
    if (!currentAgency?._id) return;
    
    try {
      const response = await crmApi.get('/collaboration/channels/overview');
      const data = response.data;
      if (data.success) {
        setOverview(data.data);
      }
    } catch (error) {
      console.error('Failed to load overview:', error);
    }
  };

  const loadChannels = async () => {
    if (!currentAgency?._id) return;
    
    setLoading(true);
    try {
      const response = await crmApi.get('/collaboration/channels');
      const data = response.data;
      if (data.success) {
        // Ensure data.data is an array
        let channelsList = [];
        
        if (Array.isArray(data.data)) {
          channelsList = data.data;
        } else if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
          // If data.data is an object, check if it has a channels property or is a single channel
          if (data.data.channels && Array.isArray(data.data.channels)) {
            channelsList = data.data.channels;
          } else if (data.data._id) {
            // Single channel object
            channelsList = [data.data];
          }
        }
        setChannels(channelsList);
        
        // Select first channel if none selected
        if (channelsList.length > 0 && !selectedChannel) {
          setSelectedChannel(channelsList[0]);
        }
      } else {
        console.error('[CollaborationHub] API returned success: false', data);
      }
    } catch (error) {
      console.error('Failed to load channels:', error);
      toast.error('Failed to load channels');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (channelId, showLoader = true) => {
    if (!channelId || !currentAgency?._id) return;
    
    if (showLoader) {
      setMessageLoading(true);
    }
    
    try {
      const response = await crmApi.get(`/collaboration/channels/${channelId}/messages`, {
        params: { limit: 50 }
      });
      const data = response.data;
      if (data.success) {
        setMessages(data.data.messages || []);
        // Mark messages as read
        if (data.data.messages && data.data.messages.length > 0) {
          data.data.messages.forEach(msg => {
            markMessageAsRead(msg._id);
          });
        }
      } else {
        console.error('Failed to load messages:', data.message);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      if (showLoader) {
        setMessageLoading(false);
      }
    }
  };

  const markMessageAsRead = async (messageId) => {
    try {
      await crmApi.post(`/collaboration/${messageId}/read`);
    } catch (error) {
      // Silently fail
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageContent.trim() || !selectedChannel || sending) return;
    
    setSending(true);
    try {
      const response = await crmApi.post(`/collaboration/channels/${selectedChannel._id}/messages`, {
        content: messageContent.trim(),
        mentions: []
      });
      
      const data = response.data;
      if (data.success) {
        setMessageContent('');
        // Reload messages
        await loadMessages(selectedChannel._id, false);
        // Reload channels to update last message time
        await loadChannels();
        // Reload overview
        await loadOverview();
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleCreateChannel = async (name, description, type) => {
    if (!name.trim()) {
      toast.error('Channel name is required');
      return false;
    }
    
    try {
      const response = await crmApi.post('/collaboration/channels', {
        name: name.trim(),
        description: description.trim(),
        type: type
      });
      
      const data = response.data;
      if (data.success) {
        toast.success('Channel created successfully!');
        await loadChannels();
        setSelectedChannel(data.data);
        return true;
      } else {
        throw new Error(data.message || 'Failed to create channel');
      }
    } catch (error) {
      console.error('Failed to create channel:', error);
      toast.error('Failed to create channel');
      return false;
    }
  };

  const openCreateChannelDialog = () => {
    const dialogId = openDialog({
      isOpen: true,
      children: (
        <CreateChannelDialogContent
          onClose={() => closeDialog(dialogId)}
          onCreate={(name, description, type) => {
            const success = handleCreateChannel(name, description, type);
            if (success) {
              closeDialog(dialogId);
            }
          }}
        />
      )
    });
  };

  const loadAvailableMembers = async (channelId) => {
    if (!channelId || !currentAgency?._id) return;
    
    try {
      const response = await crmApi.get(`/collaboration/channels/${channelId}/available-members`);
      const data = response.data;
      if (data.success) {
        setAvailableMembers(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load available members:', error);
    }
  };

  const handleAddMembers = async (userIds) => {
    if (!selectedChannel || !userIds || userIds.length === 0) return;
    
    setManagingMembers(true);
    try {
      const response = await crmApi.post(`/collaboration/channels/${selectedChannel._id}/members`, { userIds });
      const data = response.data;
      if (data.success) {
        toast.success(data.message || 'Members added successfully');
        // Update selected channel immediately with updated member list
        if (data.data && data.data.members) {
          setSelectedChannel(prev => ({
            ...prev,
            members: data.data.members
          }));
        } else {
          // Fallback: add to current state immediately
          setSelectedChannel(prev => ({
            ...prev,
            members: [...(prev.members || []), ...availableMembers.filter(m => userIds.includes(m._id || m.toString()))]
          }));
        }
        setShowMembersDialog(false);
        setAvailableMembers([]);
        // Reload channels to get updated member list
        await loadChannels();
      } else {
        toast.error(data.message || 'Failed to add members');
      }
    } catch (error) {
      console.error('Failed to add members:', error);
      toast.error('Failed to add members');
    } finally {
      setManagingMembers(false);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedChannel) return;
    
    setManagingMembers(true);
    try {
      const response = await crmApi.delete(`/collaboration/channels/${selectedChannel._id}/members/${userId}`);
      const data = response.data;
      if (data.success) {
        toast.success('Member removed successfully');
        // Update selected channel immediately with updated member list
        if (data.data && data.data.members) {
          setSelectedChannel(prev => ({
            ...prev,
            members: data.data.members
          }));
        } else {
          // Fallback: remove from current state immediately
          setSelectedChannel(prev => ({
            ...prev,
            members: (prev.members || []).filter(m => {
              const memberId = m._id || m.toString();
              return memberId !== userId && memberId.toString() !== userId.toString();
            })
          }));
        }
        // Reload channels to get updated member list
        await loadChannels();
        // If members dialog is open, refresh the available list so the removed user appears under Add Members
        if (showMembersDialog) {
          await loadAvailableMembers(selectedChannel._id);
        }
      } else {
        toast.error(data.message || 'Failed to remove member');
      }
    } catch (error) {
      console.error('Failed to remove member:', error);
      toast.error('Failed to remove member');
    } finally {
      setManagingMembers(false);
    }
  };

  const openMembersDialog = async () => {
    if (!selectedChannel) return;
    setShowMembersDialog(true);
    await loadAvailableMembers(selectedChannel._id);
  };

  const canDeleteChannel = (channel) => {
    if (!channel || !user) return false;
    const isCreator = channel.createdBy?._id === user._id || channel.createdBy?.toString() === user._id?.toString();
    // Check if user is admin
    const isAdmin = user.isSuperadmin || 
      (currentAgency && user.agencyMemberships?.some(m => 
        m.agencyId?.toString() === currentAgency._id?.toString() && 
        m.role === 'agency_admin' && 
        m.status === 'active'
      ));
    return isCreator || isAdmin;
  };

  const handleDeleteChannel = async () => {
    if (!selectedChannel) return;
    
    // Show confirmation dialog
    const dialogId = openDialog({
      isOpen: true,
      children: (
        <DeleteChannelDialogContent
          channelName={selectedChannel.name}
          onClose={() => closeDialog(dialogId)}
          onConfirm={async () => {
            closeDialog(dialogId);
            await performDeleteChannel();
          }}
        />
      )
    });
  };

  const performDeleteChannel = async () => {
    if (!selectedChannel) return;
    
    setDeletingChannel(true);
    try {
      const response = await crmApi.delete(`/collaboration/channels/${selectedChannel._id}`);
      const data = response.data;
      if (data.success) {
        toast.success('Channel deleted successfully');
        // Clear selected channel
        setSelectedChannel(null);
        // Reload channels list
        await loadChannels();
        // Reload overview
        await loadOverview();
      } else {
        toast.error(data.message || 'Failed to delete channel');
      }
    } catch (error) {
      console.error('Failed to delete channel:', error);
      toast.error('Failed to delete channel');
    } finally {
      setDeletingChannel(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatFullTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const groupMessagesByUser = (messages) => {
    const grouped = [];
    let currentGroup = null;

    messages.forEach((message, index) => {
      const isSameUser = currentGroup && 
        currentGroup.user._id === message.author._id &&
        new Date(message.createdAt) - new Date(currentGroup.messages[currentGroup.messages.length - 1].createdAt) < 5 * 60 * 1000; // 5 minutes

      if (isSameUser) {
        currentGroup.messages.push(message);
      } else {
        if (currentGroup) {
          grouped.push(currentGroup);
        }
        currentGroup = {
          user: message.author,
          messages: [message],
          isCurrentUser: message.author._id === user?._id
        };
      }
    });

    if (currentGroup) {
      grouped.push(currentGroup);
    }

    return grouped;
  };

  // Filter channels based on search
  const filteredChannels = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return channels;
    }
    const query = searchQuery.toLowerCase();
    return channels.filter(channel =>
      channel.name.toLowerCase().includes(query) ||
      (channel.description && channel.description.toLowerCase().includes(query))
    );
  }, [searchQuery, channels]);

  const groupedMessages = groupMessagesByUser(messages);

  return (
    <FeatureGate feature="advancedIntegrations" requiredTier="Enterprise">
      <div className="h-[calc(100vh-140px)] flex flex-col -mx-4 sm:-mx-6 lg:-mx-8 -mt-6 mb-0">
        {/* Main Content */}
        <div className="flex gap-0 flex-1 min-h-0 bg-background rounded-t-xl overflow-hidden border-x border-t border-border border-b-0 shadow-lg">
          {/* Channels Sidebar */}
          <div className="w-80 bg-card border-r border-border flex flex-col flex-shrink-0 pl-4 sm:pl-6 lg:pl-8 pt-6">
            {/* Sidebar Header */}
            <div className="pr-4 pb-4 border-b border-border">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-foreground">Channels</h2>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={openCreateChannelDialog}
                  className="h-7 w-7 p-0"
                  title="Create Channel"
                >
                  <PlusIcon className="h-4 w-4" />
                </Button>
              </div>

              {/* Search */}
              <div className="relative mb-3">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search channels..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Overview Stats */}
              {overview && (
                <div className="flex items-center space-x-4 text-xs text-muted-foreground pt-2 border-t border-border">
                  <div className="flex items-center space-x-1">
                    <HashtagIcon className="h-3 w-3" />
                    <span className="font-medium">{overview.activeChannels || 0}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <BellIcon className="h-3 w-3" />
                    <span className="font-medium">{overview.unreadMessages || 0}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <UserGroupIcon className="h-3 w-3" />
                    <span className="font-medium">{overview.teamMembers || 0}</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Channels List */}
            <div className="flex-1 overflow-y-auto scrollbar-thin pr-2 pb-2">
              {loading ? (
                <div className="flex items-center justify-center h-32">
                  <ArrowPathIcon className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : filteredChannels.length === 0 ? (
                <div className="p-6 text-center">
                  <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-sm font-medium text-foreground mb-1">
                    {searchQuery ? 'No channels found' : 'No channels yet'}
                  </p>
                  <p className="text-xs text-muted-foreground mb-4">
                    {searchQuery ? 'Try a different search' : 'Create your first channel to get started!'}
                  </p>
                  {!searchQuery && (
                    <Button
                      size="sm"
                      onClick={openCreateChannelDialog}
                      style={{ backgroundColor: branding.primaryColor }}
                    >
                      <PlusIcon className="h-4 w-4 mr-1" />
                      Create Channel
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-1">
                  {filteredChannels.map((channel) => (
                    <button
                      key={channel._id}
                      onClick={() => setSelectedChannel(channel)}
                      className={`w-full text-left p-3 rounded-xl transition-all duration-200 group ${
                        selectedChannel?._id === channel._id
                          ? 'bg-gradient-to-r shadow-md'
                          : 'hover:bg-muted/70'
                      }`}
                      style={selectedChannel?._id === channel._id ? {
                        background: `linear-gradient(to right, ${branding.primaryColor}15, ${branding.primaryColor}05)`,
                        borderLeft: `3px solid ${branding.primaryColor}`
                      } : {}}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2 flex-1 min-w-0">
                          {channel.type === 'private' ? (
                            <LockClosedIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
                          ) : (
                            <HashtagIcon className={`h-4 w-4 flex-shrink-0 ${
                              selectedChannel?._id === channel._id ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                            } transition-colors`} />
                          )}
                          <span className={`font-semibold text-sm truncate ${
                            selectedChannel?._id === channel._id ? 'text-foreground' : 'text-muted-foreground group-hover:text-foreground'
                          } transition-colors`}>
                            {channel.name}
                          </span>
                        </div>
                        {channel.messageCount > 0 && (
                          <Badge variant="secondary" className="text-xs flex-shrink-0">
                            {channel.messageCount}
                          </Badge>
                        )}
                      </div>
                      {channel.description && (
                        <p className="text-xs text-muted-foreground truncate mt-1 ml-6">
                          {channel.description}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div className="flex-1 flex flex-col min-w-0 pr-4 sm:pr-6 lg:pr-8">
            {selectedChannel ? (
              <>
                {/* Channel Header */}
                <div className="p-4 border-b border-border bg-card flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-lg bg-muted">
                        {selectedChannel.type === 'private' ? (
                          <LockClosedIcon className="h-5 w-5" style={{ color: branding.primaryColor }} />
                        ) : (
                          <HashtagIcon className="h-5 w-5" style={{ color: branding.primaryColor }} />
                        )}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-foreground">{selectedChannel.name}</h3>
                        {selectedChannel.description && (
                          <p className="text-xs text-muted-foreground mt-0.5">{selectedChannel.description}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge variant="secondary" className="text-xs">
                        {selectedChannel.type === 'public' ? '🌐 Public' : '🔒 Private'}
                      </Badge>
                      {selectedChannel.members && Array.isArray(selectedChannel.members) && (
                        <Badge 
                          variant="outline" 
                          className="text-xs cursor-pointer hover:bg-muted transition-colors"
                          onClick={openMembersDialog}
                          title="Manage members"
                        >
                          <UserGroupIcon className="h-3 w-3 mr-1" />
                          {selectedChannel.members.length}
                        </Badge>
                      )}
                      {canDeleteChannel(selectedChannel) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleDeleteChannel}
                          disabled={deletingChannel}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Delete channel"
                        >
                          {deletingChannel ? (
                            <ArrowPathIcon className="h-4 w-4 animate-spin" />
                          ) : (
                            <TrashIcon className="h-4 w-4" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Messages */}
                <div 
                  ref={messagesContainerRef}
                  className="flex-1 overflow-y-auto scrollbar-thin p-6 space-y-6 bg-gradient-to-b from-background to-muted/10"
                  onScroll={handleScroll}
                >
                  {messageLoading && messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center">
                        <ArrowPathIcon className="h-8 w-8 animate-spin mx-auto mb-3 text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">Loading messages...</p>
                      </div>
                    </div>
                  ) : groupedMessages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="text-center max-w-md">
                        <div className="p-4 rounded-full bg-gradient-to-br from-primary/10 to-primary/5 inline-block mb-4">
                          <ChatBubbleLeftRightIcon className="h-12 w-12" style={{ color: branding.primaryColor }} />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-2">Start the conversation</h3>
                        <p className="text-sm text-muted-foreground mb-4">
                          This is the beginning of the <strong>{selectedChannel.name}</strong> channel.
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Send a message to get things started!
                        </p>
                      </div>
                    </div>
                  ) : (
                    <>
                      {groupedMessages.map((group, groupIndex) => {
                        const showAvatar = !group.isCurrentUser;
                        const firstMessage = group.messages[0];
                        
                        return (
                          <div
                            key={`${group.user._id}-${groupIndex}`}
                            className={`flex ${group.isCurrentUser ? 'justify-end' : 'justify-start'} items-end space-x-2 group`}
                          >
                            {/* Avatar for other users */}
                            {showAvatar && (
                              <>
                                {group.user.profileImage ? (
                                  <img
                                    src={group.user.profileImage}
                                    alt={group.user.name || 'User'}
                                    className="h-10 w-10 rounded-full object-cover flex-shrink-0 shadow-md ring-2 ring-background"
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      if (e.target.nextElementSibling) {
                                        e.target.nextElementSibling.style.display = 'flex';
                                      }
                                    }}
                                  />
                                ) : null}
                                <div 
                                  className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md ring-2 ring-background"
                                  style={{ 
                                    backgroundColor: branding.primaryColor,
                                    display: group.user.profileImage ? 'none' : 'flex'
                                  }}
                                >
                                  {group.user.name?.charAt(0).toUpperCase() || 'U'}
                                </div>
                              </>
                            )}
                            
                            <div className={`flex flex-col ${group.isCurrentUser ? 'items-end' : 'items-start'} max-w-[65%] space-y-1`}>
                              {/* User name and time for other users */}
                              {!group.isCurrentUser && (
                                <div className="flex items-center space-x-2 px-1">
                                  <span className="text-xs font-semibold text-foreground">
                                    {group.user.name || 'Unknown'}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {formatFullTime(firstMessage.createdAt)}
                                  </span>
                                </div>
                              )}
                              
                              {/* Message bubbles */}
                              <div className="space-y-1">
                                {group.messages.map((message, msgIndex) => {
                                  const isLast = msgIndex === group.messages.length - 1;
                                  
                                  return (
                                    <div
                                      key={message._id}
                                      className={`relative ${
                                        group.isCurrentUser
                                          ? 'bg-gradient-to-br rounded-2xl rounded-tr-md shadow-md hover:shadow-lg transition-shadow'
                                          : 'bg-card border border-border rounded-2xl rounded-tl-md shadow-sm hover:shadow-md transition-shadow'
                                      }`}
                                      style={group.isCurrentUser ? {
                                        background: `linear-gradient(135deg, ${branding.primaryColor}, ${branding.primaryColor}dd)`,
                                        color: 'white'
                                      } : {}}
                                    >
                                      <div className="px-4 py-2.5">
                                        <p className={`text-sm leading-relaxed whitespace-pre-wrap ${
                                          group.isCurrentUser ? 'text-white' : 'text-foreground'
                                        }`}>
                                          {message.content.text}
                                        </p>
                                        
                                        {/* Attachments */}
                                        {message.content.attachments && message.content.attachments.length > 0 && (
                                          <div className="mt-2 space-y-1">
                                            {message.content.attachments.map((att, idx) => (
                                              <a
                                                key={idx}
                                                href={att.url.startsWith('http') ? att.url : `${getApiUrl()}${att.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`inline-flex items-center space-x-1 text-xs px-2 py-1 rounded-md transition-colors ${
                                                  group.isCurrentUser 
                                                    ? 'bg-white/20 hover:bg-white/30 text-white' 
                                                    : 'bg-muted hover:bg-muted/80 text-foreground'
                                                }`}
                                              >
                                                <PaperClipIcon className="h-3 w-3" />
                                                <span>{att.originalName}</span>
                                              </a>
                                            ))}
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* Time for current user */}
                                      {group.isCurrentUser && isLast && (
                                        <div className="absolute -bottom-5 right-0 text-xs text-muted-foreground px-1">
                                          {formatTime(message.createdAt)}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            
                            {/* Spacer for alignment */}
                            {group.isCurrentUser && <div className="w-10 flex-shrink-0" />}
                          </div>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Message Input */}
                <div className="p-4 border-t border-border bg-card flex-shrink-0 rounded-b-xl">
                  <form onSubmit={handleSendMessage} className="flex items-end space-x-3">
                    <div className="flex-1 relative">
                      <Textarea
                        ref={textareaRef}
                        value={messageContent}
                        onChange={(e) => setMessageContent(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e);
                          }
                        }}
                        placeholder={`Message #${selectedChannel.name}`}
                        rows={1}
                        className="pr-12 min-h-[44px] resize-none"
                      />
                      <div className="absolute right-3 bottom-3 flex items-center space-x-2">
                        <button
                          type="button"
                          className="p-1.5 hover:bg-muted rounded-lg transition-colors"
                          title="Add emoji"
                        >
                          <FaceSmileIcon className="h-5 w-5 text-muted-foreground" />
                        </button>
                        <button
                          type="button"
                          className="p-1.5 hover:bg-muted rounded-lg transition-colors"
                          title="Attach file"
                        >
                          <PaperClipIcon className="h-5 w-5 text-muted-foreground" />
                        </button>
                      </div>
                    </div>
                    <Button
                      type="submit"
                      disabled={!messageContent.trim() || sending}
                      size="lg"
                      className="px-6 shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ 
                        backgroundColor: branding.primaryColor,
                        minWidth: '60px'
                      }}
                    >
                      {sending ? (
                        <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      ) : (
                        <PaperAirplaneIcon className="h-5 w-5" />
                      )}
                    </Button>
                  </form>
                  <p className="text-xs text-muted-foreground mt-2 px-1">
                    Press <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Enter</kbd> to send, <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs">Shift + Enter</kbd> for new line
                  </p>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-background via-muted/10 to-background">
                <div className="text-center max-w-md px-6">
                  <div className="p-6 rounded-3xl bg-gradient-to-br from-primary/10 to-primary/5 inline-block mb-6">
                    <SparklesIcon className="h-16 w-16" style={{ color: branding.primaryColor }} />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground mb-3">Welcome to Collaboration Hub</h3>
                  <p className="text-muted-foreground mb-6">
                    Select a channel from the sidebar to start collaborating with your team, or create a new channel to get started.
                  </p>
                  <Button
                    onClick={openCreateChannelDialog}
                    size="lg"
                    style={{ backgroundColor: branding.primaryColor }}
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    Create Your First Channel
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Members Management Dialog */}
      {showMembersDialog && selectedChannel && (
        <MembersDialog
          channel={selectedChannel}
          availableMembers={availableMembers}
          onClose={() => {
            setShowMembersDialog(false);
            setAvailableMembers([]);
          }}
          onAddMembers={handleAddMembers}
          onRemoveMember={handleRemoveMember}
          managingMembers={managingMembers}
        />
      )}
    </FeatureGate>
  );
};

// Create Channel Dialog Component
const CreateChannelDialogContent = ({ onClose, onCreate }) => {
  const { branding } = useBranding();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('public');
  const [creating, setCreating] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    
    setCreating(true);
    const success = await onCreate(name, description, type);
    setCreating(false);
    
    if (success) {
      setName('');
      setDescription('');
      setType('public');
    }
  };

  return (
    <div role="dialog" aria-modal="true">
      <DialogHeader title="Create New Channel" onClose={onClose} />
      <DialogBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Channel Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              placeholder="e.g., general, announcements"
              value={name}
              onChange={(e) => setName(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              autoFocus
              required
            />
            <p className="text-xs text-muted-foreground mt-1">
              Channel names can only contain lowercase letters, numbers, and hyphens
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Description (Optional)
            </label>
            <Textarea
              placeholder="What's this channel about?"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="2"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Channel Type
            </label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
            >
              <option value="public">🌐 Public - Anyone in the agency can join</option>
              <option value="private">🔒 Private - Only invited members can join</option>
            </select>
          </div>
        </form>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={creating}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!name.trim() || creating}
          style={{ backgroundColor: branding?.primaryColor || '#3B82F6' }}
        >
          {creating ? (
            <>
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <PlusIcon className="h-4 w-4 mr-2" />
              Create Channel
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
};

// Members Management Dialog Component
const MembersDialog = ({ channel, availableMembers, onClose, onAddMembers, onRemoveMember, managingMembers }) => {
  const { branding } = useBranding();
  const { user } = useCRM();
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const isCreator = channel?.createdBy?._id === user?._id || channel?.createdBy?.toString() === user?._id?.toString();

  const handleToggleUser = (userId) => {
    const isPublicChannel = channel?.type === 'public';
    if (isPublicChannel) return; // Disable selection for public channels
    setSelectedUserIds(prev => 
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAdd = async () => {
    if (selectedUserIds.length === 0) {
      toast.error('Please select at least one member to add');
      return;
    }
    await onAddMembers(selectedUserIds);
    setSelectedUserIds([]);
  };

  const canRemove = (memberId) => {
    if (channel?.type === 'public') return false; // Cannot remove from public channels
    // Users can remove themselves, or creator can remove anyone except themselves
    const isSelf = memberId === user?._id || memberId.toString() === user?._id?.toString();
    const isMemberCreator = memberId === channel?.createdBy?._id || memberId.toString() === channel?.createdBy?._id?.toString();
    
    if (isSelf) return true; // Can always remove self
    if (isMemberCreator) return false; // Cannot remove creator
    if (isCreator) return true; // Creator can remove anyone
    return false;
  };

  const currentMembers = channel?.members || [];
  const currentMemberIds = currentMembers.map(m => m._id || m.toString());
  const isPublicChannel = channel?.type === 'public';
  
  // For public channels, show all agency admins and users from availableMembers
  // For private channels, show current members
  const displayMembers = isPublicChannel ? availableMembers : currentMembers;

  return (
    <div 
      className="fixed inset-0 z-[150] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div 
        className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[80vh] flex flex-col relative"
        onClick={(e) => e.stopPropagation()}
      >
        <DialogHeader 
          title={`Manage Members - ${channel?.name}`}
          onClose={onClose}
        />
        <DialogBody className="overflow-y-auto scrollbar-thin">
          <div className="space-y-6">
            {/* Info about public channels */}
            {isPublicChannel && (
              <div className="p-4 rounded-lg bg-blue-50/50 dark:bg-blue-950/10 border border-blue-200 dark:border-blue-900">
                <p className="text-sm text-blue-900 dark:text-blue-300">
                  <strong>Public Channel:</strong> All agency admins and users can see and engage with this channel. No need to add or remove members.
                </p>
              </div>
            )}
            
            {/* Current Members / Agency Members */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                <UserGroupIcon className="h-4 w-4 mr-2" />
                {isPublicChannel ? 'Agency Members' : `Current Members (${currentMembers.length})`}
                {isPublicChannel && ` (${displayMembers.length})`}
              </h3>
              {displayMembers.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">
                  {isPublicChannel ? 'No agency members found' : 'No members yet'}
                </p>
              ) : (
                <div className="space-y-2">
                  {displayMembers.map((member) => {
                    const memberId = member._id || member.toString();
                    const memberName = member.name || 'Unknown';
                    const memberEmail = member.email || '';
                    const canRemoveMember = canRemove(memberId);
                    
                    return (
                      <div
                        key={memberId}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 border border-border"
                      >
                        <div className="flex items-center space-x-3">
                          {member.profileImage ? (
                            <img
                              src={member.profileImage}
                              alt={memberName}
                              className="w-8 h-8 rounded-full object-cover border-2 border-border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                if (e.target.nextElementSibling) {
                                  e.target.nextElementSibling.style.display = 'flex';
                                }
                              }}
                            />
                          ) : null}
                          <div 
                            className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white text-xs font-semibold"
                            style={{ display: member.profileImage ? 'none' : 'flex' }}
                          >
                            {memberName.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-sm font-medium text-foreground">{memberName}</p>
                            {memberEmail && (
                              <p className="text-xs text-muted-foreground">{memberEmail}</p>
                            )}
                            {memberId === channel?.createdBy?._id || memberId.toString() === channel?.createdBy?.toString() ? (
                              <Badge variant="secondary" className="text-xs mt-1">Creator</Badge>
                            ) : null}
                          </div>
                        </div>
                        {canRemoveMember && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onRemoveMember(memberId)}
                            disabled={managingMembers}
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add Members (only for private channels) */}
            {channel?.type === 'private' && availableMembers.length > 0 && (
              <div>
                <Separator className="my-4" />
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center">
                  <UserPlusIcon className="h-4 w-4 mr-2" />
                  Add Members ({availableMembers.length} available)
                </h3>
                <div className="space-y-2 max-h-64 overflow-y-auto scrollbar-thin">
                  {availableMembers.map((member) => {
                    const memberId = member._id || member.toString();
                    const isSelected = selectedUserIds.includes(memberId);
                    
                    return (
                      <button
                        key={memberId}
                        onClick={() => handleToggleUser(memberId)}
                        className={`w-full text-left p-3 rounded-lg border transition-all ${
                          isSelected
                            ? 'bg-primary/10 border-primary'
                            : 'bg-muted/50 border-border hover:bg-muted'
                        }`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center relative ${
                            isSelected
                              ? 'border-primary bg-primary/20'
                              : 'border-border'
                          }`}>
                            {isSelected && (
                              <div className="w-2 h-2 rounded-full bg-primary" />
                            )}
                          </div>
                          {member.profileImage ? (
                            <img
                              src={member.profileImage}
                              alt={member.name}
                              className="w-8 h-8 rounded-full object-cover border-2 border-border"
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center text-white text-xs font-semibold">
                              {member.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-sm font-medium text-foreground">{member.name}</p>
                            {member.email && (
                              <p className="text-xs text-muted-foreground">{member.email}</p>
                            )}
                          </div>
                          {member.role && (
                            <Badge variant="outline" className="text-xs">
                              {member.role === 'agency_user' ? 'Agency User' : 
                               member.role === 'agency_admin' ? 'Agency Admin' :
                               member.role === 'client' ? 'Client' :
                               member.role === 'account_contact' ? 'Account Contact' :
                               member.role === 'superadmin' ? 'Superadmin' :
                               member.role}
                            </Badge>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
                {selectedUserIds.length > 0 && (
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={handleAdd}
                      disabled={managingMembers || selectedUserIds.length === 0}
                      style={{ backgroundColor: branding?.primaryColor || '#3B82F6' }}
                    >
                      {managingMembers ? (
                        <>
                          <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <UserPlusIcon className="h-4 w-4 mr-2" />
                          Add {selectedUserIds.length} Member(s)
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </div>
            )}

            {availableMembers.length === 0 && channel?.type === 'private' && (
              <div className="text-center py-4">
                <p className="text-sm text-muted-foreground">All team members are already in this channel</p>
              </div>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={managingMembers}>
            Close
          </Button>
        </DialogFooter>
      </div>
    </div>
  );
};

// Delete Channel Confirmation Dialog Component
const DeleteChannelDialogContent = ({ channelName, onClose, onConfirm }) => {
  const { branding } = useBranding();
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    setDeleting(true);
    await onConfirm();
    setDeleting(false);
  };

  return (
    <div role="dialog" aria-modal="true">
      <DialogHeader title="Delete Channel" onClose={onClose} />
      <DialogBody>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <div className="p-2 rounded-full bg-destructive/10">
              <TrashIcon className="h-5 w-5 text-destructive" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-foreground font-medium mb-1">
                Are you sure you want to delete this channel?
              </p>
              <p className="text-sm text-muted-foreground">
                The channel <strong>#{channelName}</strong> will be permanently deleted. This action cannot be undone.
              </p>
            </div>
          </div>
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3">
            <p className="text-xs text-yellow-800 dark:text-yellow-200">
              <strong>Note:</strong> All messages in this channel will be archived. Team members will no longer be able to access this channel.
            </p>
          </div>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={deleting}
          className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
        >
          {deleting ? (
            <>
              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
              Deleting...
            </>
          ) : (
            <>
              <TrashIcon className="h-4 w-4 mr-2" />
              Delete Channel
            </>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
};

export default CollaborationHub;
